import { supabase } from '../lib/supabase';
import { calculateKPIs } from './budgetKPIService';

/**
 * Service for managing project costs (budget tracking)
 * Handles CRUD operations for project_costs table
 */

/**
 * Convert snake_case database fields to camelCase for React
 */
const toCamelCase = (data, contractActual, expensesActual) => {
  if (!data) return null;
  if (Array.isArray(data)) return data?.map(toCamelCase);

  const computedContractActual = contractActual !== undefined ? contractActual : 0;
  const computedExpensesActual = expensesActual !== undefined ? expensesActual : 0;
  const computedActual = contractActual !== undefined ? computedContractActual + computedExpensesActual : (data?.actual || 0);

  return {
    id: data?.id,
    projectId: data?.project_id,
    category: data?.category,
    costCode: data?.cost_code,
    description: data?.description,
    budget: data?.budget || 0,
    commitmentActual: computedContractActual,
    expensesActual: computedExpensesActual,
    actual: computedActual,
    forecast: data?.forecast || 0,
    createdAt: data?.created_at,
    updatedAt: data?.updated_at
  };
};

/**
 * Convert camelCase React fields to snake_case for database
 */
const toSnakeCase = (data) => {
  if (!data) return null;
  
  const result = {};
  
  if (data?.projectId !== undefined) result.project_id = data?.projectId;
  if (data?.category !== undefined) result.category = data?.category;
  if (data?.costCode !== undefined) result.cost_code = data?.costCode;
  if (data?.description !== undefined) result.description = data?.description;
  if (data?.budget !== undefined) result.budget = data?.budget;
  // NOTE: 'actual' is intentionally excluded — it is a computed field derived from
  // SUM(subcontracts.contract_value) and must never be stored in project_costs
  if (data?.forecast !== undefined) result.forecast = data?.forecast;
  
  return result;
};

/**
 * Compute P/L and P/L% for a single cost-code row.
 * Shared by renderCostCodeRow and calcGroupSubtotals in BudgetPage.
 * @param {{ budget: number, actual: number, forecast: number }} item
 * @returns {{ pl: number, plPercent: number }}
 */
export const computeRowPL = (item) => {
  const budget = item?.budget || 0;
  const actual = item?.actual || 0;
  const forecast = item?.forecast || 0;
  const pl = budget > 0 ? budget - (actual + forecast) : 0;
  const plPercent = budget > 0 ? (pl / budget) * 100 : 0;
  return { pl, plPercent };
};

/**
 * Calculate total budget from all project_costs and update projects.contract_value
 * @param {string} projectId - UUID of the project
 */
const updateProjectContractValue = async (projectId) => {
  try {
    // Calculate sum of all budget values for this project
    const { data: costs, error: fetchError } = await supabase?.from('project_costs')?.select('budget')?.eq('project_id', projectId);

    if (fetchError) throw fetchError;

    // Sum all budget values
    const totalBudget = costs?.reduce((sum, cost) => sum + (parseFloat(cost?.budget) || 0), 0) || 0;

    // Update projects.contract_value
    const { error: updateError } = await supabase?.from('projects')?.update({ contract_value: totalBudget })?.eq('id', projectId);

    if (updateError) throw updateError;

    console.log(`[Contract Value Sync] Updated project ${projectId} contract_value to ${totalBudget}`);
  } catch (error) {
    console.error('Error updating project contract value:', error?.message);
    // Don't throw - allow the cost operation to succeed even if sync fails
  }
};

/**
 * Build the commitment (Engagement) map keyed by project_cost_id.
 *
 * Engagement = fixed contracts + Time & Materials invoiced so far.
 *   - Fixed term:  SUM(subcontracts.contract_value) where pricing_model = 'fixed'
 *                  and status NOT IN ('to_assign','cancelled').
 *   - T&M term:    SUM(sub_invoice_lines.amount) for line_type = 'cost_code', whose
 *                  parent sub_invoice.status <> 'rejected' and whose owning subcontract
 *                  has pricing_model IN ('labor_material','material_supply').
 *                  Link chain: sub_invoice_lines -> sub_invoice_id
 *                  -> sub_invoices.subcontract_id -> subcontracts.pricing_model.
 *
 * The two terms are mutually exclusive by the owning contract's pricing_model, so a
 * fixed contract's invoices are never double-counted (they stay out of the T&M term),
 * and a T&M contract has no fixed contract_value to add to the fixed term.
 *
 * Extras/credits (line_type = 'extra_credit') are intentionally excluded — they belong
 * to the Extras & Credits tab, not to Engagement. Direct expenses (project_expenses)
 * are handled separately as the "Dépenses" column and are NOT part of this map.
 *
 * Returns both the aggregated total per project_cost_id (`totals`, identical to the
 * value shown in the Engagement column) and a per-contract breakdown (`breakdown`)
 * used to explain that total in a hover tooltip. The breakdown amounts reconcile
 * exactly to `totals` (zero-amount entries are omitted).
 *
 * @param {string} projectId - UUID of the project
 * @returns {Promise<{ totals: Object, breakdown: Object }>}
 *   totals: project_cost_id -> commitment amount
 *   breakdown: project_cost_id -> Array<{ subcontractId, subcontractorName, pricingModel, amount }>
 */
// Shared selects so the single-project and bulk paths stay identical
const SUBCONTRACT_COMMITMENT_SELECT = 'id, project_cost_id, contract_value, status, pricing_model, subcontractors(company_name)';
const TM_LINES_SELECT = 'amount, project_cost_id, sub_invoices!inner(project_id, status, subcontract_id)';
const TM_MODELS = ['labor_material', 'material_supply'];

/**
 * Pure commitment computation over already-fetched rows (no I/O).
 * Keyed by project_cost_id, which is globally unique — so the same function is
 * correct whether it receives one project's rows or the whole portfolio's.
 * @param {Array} subcontracts - rows selected with SUBCONTRACT_COMMITMENT_SELECT
 * @param {Array} tmLines - rows selected with TM_LINES_SELECT (non-rejected, cost_code)
 * @returns {{ totals: Object, breakdown: Object }}
 */
const computeCommitment = (subcontracts, tmLines) => {
  // Metadata per subcontract id, to classify each invoice line's owning contract
  const subById = {};
  subcontracts?.forEach(sc => {
    if (sc?.id) {
      subById[sc.id] = {
        pricingModel: sc?.pricing_model,
        name: sc?.subcontractors?.company_name || null
      };
    }
  });

  const totals = {};
  const breakdown = {};
  const pushEntry = (pcId, entry) => {
    // Skip zero-amount entries so the breakdown reconciles to the total exactly
    if (!pcId || !entry?.amount) return;
    if (!breakdown[pcId]) breakdown[pcId] = [];
    breakdown[pcId].push(entry);
  };

  // Term A — fixed contracts: SUM(contract_value), excluding to_assign/cancelled
  subcontracts?.forEach(sc => {
    const pcId = sc?.project_cost_id;
    const status = sc?.status;
    if (pcId && status !== 'to_assign' && status !== 'cancelled' && sc?.pricing_model === 'fixed') {
      const amount = parseFloat(sc?.contract_value) || 0;
      totals[pcId] = (totals?.[pcId] || 0) + amount;
      pushEntry(pcId, {
        subcontractId: sc?.id,
        subcontractorName: sc?.subcontractors?.company_name || null,
        pricingModel: 'fixed',
        amount
      });
    }
  });

  // Term B — T&M invoiced: only lines whose owning subcontract is labor_material/material_supply.
  // Grouped by (project_cost_id, subcontract_id) so each contributing contract is one tooltip line.
  const tmGroups = {};
  tmLines?.forEach(line => {
    const pcId = line?.project_cost_id;
    const subId = line?.sub_invoices?.subcontract_id;
    const pricing = subId ? subById?.[subId]?.pricingModel : undefined;
    if (pcId && TM_MODELS?.includes(pricing)) {
      const amount = parseFloat(line?.amount) || 0;
      totals[pcId] = (totals?.[pcId] || 0) + amount;
      const key = `${pcId}|${subId}`;
      if (!tmGroups[key]) tmGroups[key] = { pcId, subId, amount: 0 };
      tmGroups[key].amount += amount;
    }
  });
  Object.values(tmGroups)?.forEach(g => {
    pushEntry(g?.pcId, {
      subcontractId: g?.subId,
      subcontractorName: subById?.[g?.subId]?.name || null,
      pricingModel: subById?.[g?.subId]?.pricingModel,
      amount: g?.amount
    });
  });

  return { totals, breakdown };
};

const buildCommitmentMap = async (projectId) => {
  const [subcontractsResult, tmLinesResult] = await Promise.all([
    supabase?.from('subcontracts')?.select(SUBCONTRACT_COMMITMENT_SELECT)?.eq('project_id', projectId),
    // T&M invoiced: cost-code lines of non-rejected sub_invoices for this project.
    // Same join/status pattern already used in portfolioProfitService.
    supabase?.from('sub_invoice_lines')?.select(TM_LINES_SELECT)?.eq('sub_invoices.project_id', projectId)?.neq('sub_invoices.status', 'rejected')?.eq('line_type', 'cost_code')
  ]);

  if (subcontractsResult?.error) throw subcontractsResult?.error;
  // T&M lines error is non-fatal — computeCommitment falls back to fixed-only
  return computeCommitment(subcontractsResult?.data || [], tmLinesResult?.data || []);
};

/**
 * Bulk commitment for the portfolio — 2 queries TOTAL for all projects (no N+1).
 * project_cost_id is globally unique, so the flat totals map covers every project.
 * @param {Array<string>} projectIds
 * @returns {Promise<{ totals: Object, breakdown: Object }>}
 */
const buildCommitmentForProjects = async (projectIds) => {
  if (!projectIds || projectIds?.length === 0) return { totals: {}, breakdown: {} };
  const [subcontractsResult, tmLinesResult] = await Promise.all([
    supabase?.from('subcontracts')?.select(SUBCONTRACT_COMMITMENT_SELECT)?.in('project_id', projectIds),
    supabase?.from('sub_invoice_lines')?.select(TM_LINES_SELECT)?.in('sub_invoices.project_id', projectIds)?.neq('sub_invoices.status', 'rejected')?.eq('line_type', 'cost_code')
  ]);

  if (subcontractsResult?.error) throw subcontractsResult?.error;
  return computeCommitment(subcontractsResult?.data || [], tmLinesResult?.data || []);
};

/**
 * Get all cost codes for a project and category
 * @param {string} projectId - UUID of the project
 * @param {string} category - Category filter: 'construction', 'general', or 'admin'
 * @returns {Promise<Array>} Array of cost code objects
 */
export const getByProjectAndCategory = async (projectId, category) => {
  try {
    const [costsResult, commitment, expensesResult] = await Promise.all([
      supabase?.from('project_costs')?.select('*')?.eq('project_id', projectId)?.eq('category', category)?.order('cost_code', { ascending: true }),
      buildCommitmentMap(projectId),
      supabase?.from('project_expenses')?.select('cost_code, amount')?.eq('project_id', projectId)
    ]);

    if (costsResult?.error) throw costsResult?.error;

    const costs = costsResult?.data || [];
    const expenses = expensesResult?.data || [];
    const { totals = {}, breakdown = {} } = commitment || {};

    // Build a map of cost_code -> SUM(amount) from project_expenses
    const actualExpensesMap = {};
    expenses?.forEach(exp => {
      const cc = exp?.cost_code;
      if (cc) {
        actualExpensesMap[cc] = (actualExpensesMap?.[cc] || 0) + (parseFloat(exp?.amount) || 0);
      }
    });

    return costs?.map(cost => {
      const actualContract = totals?.[cost?.id] ?? 0;
      const actualExpenses = actualExpensesMap?.[cost?.cost_code] ?? 0;
      return { ...toCamelCase(cost, actualContract, actualExpenses), commitmentBreakdown: breakdown?.[cost?.id] ?? [] };
    });
  } catch (error) {
    console.error('Error fetching project costs:', error?.message);
    throw error;
  }
};

/**
 * Get all cost codes for a project (all categories)
 * @param {string} projectId - UUID of the project
 * @returns {Promise<Array>} Array of cost code objects
 */
export const getByProjectId = async (projectId) => {
  try {
    const [costsResult, commitment, expensesResult] = await Promise.all([
      supabase?.from('project_costs')?.select('*')?.eq('project_id', projectId)?.order('category', { ascending: true })?.order('created_at', { ascending: true }),
      buildCommitmentMap(projectId),
      supabase?.from('project_expenses')?.select('cost_code, amount')?.eq('project_id', projectId)
    ]);

    if (costsResult?.error) throw costsResult?.error;

    const costs = costsResult?.data || [];
    const expenses = expensesResult?.data || [];
    const { totals = {}, breakdown = {} } = commitment || {};

    // Build a map of cost_code -> SUM(amount) from project_expenses
    const actualExpensesMap = {};
    expenses?.forEach(exp => {
      const cc = exp?.cost_code;
      if (cc) {
        actualExpensesMap[cc] = (actualExpensesMap?.[cc] || 0) + (parseFloat(exp?.amount) || 0);
      }
    });

    return costs?.map(cost => {
      const actualContract = totals?.[cost?.id] ?? 0;
      const actualExpenses = actualExpensesMap?.[cost?.cost_code] ?? 0;
      return { ...toCamelCase(cost, actualContract, actualExpenses), commitmentBreakdown: breakdown?.[cost?.id] ?? [] };
    });
  } catch (error) {
    console.error('Error fetching all project costs:', error?.message);
    throw error;
  }
};

/**
 * Create a new cost code
 * @param {Object} costData - Cost code data (camelCase)
 * @returns {Promise<Object>} Created cost code object
 */
export const create = async (costData) => {
  try {
    const dbData = toSnakeCase(costData);
    
    const { data, error } = await supabase?.from('project_costs')?.insert([dbData])?.select()?.single();

    if (error) throw error;
    return toCamelCase(data);
  } catch (error) {
    console.error('Error creating project cost:', error?.message);
    throw error;
  }
};

/**
 * Update an existing cost code
 * @param {string} id - UUID of the cost code
 * @param {Object} updates - Fields to update (camelCase)
 * @returns {Promise<Object>} Updated cost code object
 */
export const update = async (id, updates) => {
  try {
    const dbUpdates = toSnakeCase(updates);
    
    const { data, error } = await supabase?.from('project_costs')?.update(dbUpdates)?.eq('id', id)?.select()?.single();

    if (error) throw error;
    return toCamelCase(data);
  } catch (error) {
    console.error('Error updating project cost:', error?.message);
    throw error;
  }
};

/**
 * Delete a cost code
 * @param {string} id - UUID of the cost code
 * @returns {Promise<void>}
 */
export const remove = async (id) => {
  try {
    const { error } = await supabase?.from('project_costs')?.delete()?.eq('id', id);

    if (error) throw error;
  } catch (error) {
    console.error('Error deleting project cost:', error?.message);
    throw error;
  }
};

/**
 * Get aggregated totals for a project
 * @param {string} projectId - UUID of the project
 * @returns {Promise<Object>} Object with totalBudget, totalActual, totalProfit
 */
export const getProjectTotals = async (projectId) => {
  try {
    const { data, error } = await supabase?.from('project_costs')?.select('budget, actual')?.eq('project_id', projectId);

    if (error) throw error;

    const totalBudget = data?.reduce((sum, cost) => sum + (parseFloat(cost?.budget) || 0), 0) || 0;
    const totalActual = data?.reduce((sum, cost) => sum + (parseFloat(cost?.actual) || 0), 0) || 0;
    const totalProfit = totalBudget - totalActual;

    return {
      totalBudget,
      totalActual,
      totalProfit
    };
  } catch (error) {
    console.error('Error calculating project totals:', error?.message);
    return {
      totalBudget: 0,
      totalActual: 0,
      totalProfit: 0
    };
  }
};

/**
 * Get aggregated totals for multiple projects
 * @param {Array<string>} projectIds - Array of project UUIDs
 * @returns {Promise<Object>} Map of projectId to totals
 */
export const getMultipleProjectTotals = async (projectIds) => {
  try {
    if (!projectIds || projectIds?.length === 0) return {};

    const { data, error } = await supabase?.from('project_costs')?.select('project_id, budget, actual')?.in('project_id', projectIds);

    if (error) throw error;

    // Group by project_id and calculate totals
    const totalsMap = {};
    data?.forEach(cost => {
      const projectId = cost?.project_id;
      if (!totalsMap?.[projectId]) {
        totalsMap[projectId] = {
          totalBudget: 0,
          totalActual: 0,
          totalProfit: 0
        };
      }
      totalsMap[projectId].totalBudget += parseFloat(cost?.budget) || 0;
      totalsMap[projectId].totalActual += parseFloat(cost?.actual) || 0;
    });

    // Calculate profit for each project
    Object.keys(totalsMap)?.forEach(projectId => {
      totalsMap[projectId].totalProfit = totalsMap?.[projectId]?.totalBudget - totalsMap?.[projectId]?.totalActual;
    });

    return totalsMap;
  } catch (error) {
    console.error('Error calculating multiple project totals:', error?.message);
    return {};
  }
};

/**
 * Get financial summary (adjustedContract, totalActualCost, liveProfit, profitMargin)
 * for multiple projects — IDENTICAL numbers to a project's Aperçu financier.
 *
 * Rather than re-deriving the formula, this reconstructs, per project, the exact
 * inputs BudgetPage feeds to calculateKPIs (the shared KPI engine) and calls it:
 *   - cost rows: budget, commitmentActual (fixed + T&M via computeCommitment),
 *                expensesActual (SUM project_expenses.amount by cost_code, all statuses),
 *                forecast
 *   - extras (contract side): type/status/costAccepted/amount
 *   - extrasCosts (cost side): commitment (signed/sent COs), expenses (approved/paid
 *                 expenses linked to the extra), forecast — mirrors extrasBudgetService
 * All data is fetched in bulk (constant number of queries, no N+1) for the whole list.
 * @param {Array<string>} projectIds - Array of project UUIDs
 * @returns {Promise<Object>} Map of projectId -> { adjustedContract, totalActualCost, liveProfit, profitMargin, revisedContractValue }
 */
export const getFinancialSummaryForProjects = async (projectIds) => {
  try {
    if (!projectIds || projectIds?.length === 0) return {};

    // 1) Bulk fetch — same inputs BudgetPage uses, for every project at once
    const [costsResult, commitment, expensesResult, extrasResult] = await Promise.all([
      supabase?.from('project_costs')?.select('id, project_id, category, cost_code, budget, forecast')?.in('project_id', projectIds)?.in('category', ['construction', 'general', 'admin']),
      buildCommitmentForProjects(projectIds),
      supabase?.from('project_expenses')?.select('project_id, cost_code, amount')?.in('project_id', projectIds),
      supabase?.from('project_extras')?.select('id, project_id, type, status, cost_accepted, amount, forecast_amount')?.in('project_id', projectIds)
    ]);

    if (costsResult?.error) throw costsResult?.error;

    const costs = costsResult?.data || [];
    const { totals: commitmentByCostId = {} } = commitment || {};
    const expenses = expensesResult?.data || [];
    const extrasRows = extrasResult?.data || [];

    // 2) Extras cost side (extrasBudgetService parity): CO commitment + linked expenses per extra
    const extraIds = extrasRows?.map(e => e?.id)?.filter(Boolean);
    let coRows = [];
    let extraExpRows = [];
    if (extraIds?.length > 0) {
      const [coResult, extraExpResult] = await Promise.all([
        supabase?.from('subcontract_change_orders')?.select('source_extra_id, amount')?.in('source_extra_id', extraIds)?.in('status', ['signed', 'sent']),
        supabase?.from('project_expenses')?.select('extra_id, total_amount')?.in('extra_id', extraIds)?.in('status', ['approved', 'paid'])
      ]);
      coRows = coResult?.data || [];
      extraExpRows = extraExpResult?.data || [];
    }
    const coByExtra = {};
    coRows?.forEach(co => { const eid = co?.source_extra_id; if (eid) coByExtra[eid] = (coByExtra?.[eid] || 0) + (parseFloat(co?.amount) || 0); });
    const expByExtra = {};
    extraExpRows?.forEach(exp => { const eid = exp?.extra_id; if (eid) expByExtra[eid] = (expByExtra?.[eid] || 0) + (parseFloat(exp?.total_amount) || 0); });

    // 3) Expenses per (project, cost_code) — all statuses, column `amount` (matches getByProjectAndCategory)
    const expensesByProjectCostCode = {};
    expenses?.forEach(exp => {
      const pid = exp?.project_id;
      const cc = exp?.cost_code;
      if (pid && cc) {
        if (!expensesByProjectCostCode[pid]) expensesByProjectCostCode[pid] = {};
        expensesByProjectCostCode[pid][cc] = (expensesByProjectCostCode?.[pid]?.[cc] || 0) + (parseFloat(exp?.amount) || 0);
      }
    });

    // 4) Group cost rows + extras per project
    const costsByProject = {};
    costs?.forEach(c => {
      const pid = c?.project_id;
      if (!costsByProject[pid]) costsByProject[pid] = [];
      costsByProject[pid].push({
        id: c?.id,
        budget: parseFloat(c?.budget) || 0,
        commitmentActual: commitmentByCostId?.[c?.id] ?? 0,
        expensesActual: expensesByProjectCostCode?.[pid]?.[c?.cost_code] ?? 0,
        forecast: parseFloat(c?.forecast) || 0
      });
    });

    const extrasByProject = {};
    const extrasCostsByProject = {};
    extrasRows?.forEach(e => {
      const pid = e?.project_id;
      if (!extrasByProject[pid]) { extrasByProject[pid] = []; extrasCostsByProject[pid] = []; }
      // Contract-side shape consumed by calculateKPIs
      extrasByProject[pid].push({ id: e?.id, type: e?.type, status: e?.status, costAccepted: e?.cost_accepted, amount: e?.amount });
      // Cost-side shape consumed by calculateKPIs (mirrors extrasBudgetService)
      extrasCostsByProject[pid].push({ id: e?.id, commitment: coByExtra?.[e?.id] || 0, expenses: expByExtra?.[e?.id] || 0, forecast: parseFloat(e?.forecast_amount) || 0 });
    });

    // 5) Run the SAME engine as BudgetPage, per project → identical numbers
    const summaryMap = {};
    projectIds?.forEach(pid => {
      const kpis = calculateKPIs({
        constructionCosts: costsByProject?.[pid] || [],
        generalCosts: [],
        adminCosts: [],
        extras: extrasByProject?.[pid] || [],
        extrasCosts: extrasCostsByProject?.[pid] || []
      });
      summaryMap[pid] = {
        adjustedContract: kpis?.adjustedContract,
        totalActualCost: kpis?.totalActualCost,
        liveProfit: kpis?.liveProfit,
        profitMargin: kpis?.profitMargin,
        revisedContractValue: kpis?.adjustedContract
      };
    });

    return summaryMap;
  } catch (error) {
    console.error('Error fetching financial summary for projects:', error?.message);
    return {};
  }
};

export const projectCostService = {
  getByProjectAndCategory,
  getByProjectId,
  create,
  update,
  remove,
  getProjectTotals,
  getMultipleProjectTotals,
  getFinancialSummaryForProjects
};

export default projectCostService;